import { MutableRefObject, PointerEvent as RPE, useCallback, useEffect, useRef } from "react";
import type { AnimationItem } from "lottie-web";
import { Pause, Play, SkipBack, SkipForward } from "lucide-react";

/* شريط زمني على نمط برامج المونتاج:
   - المسطرة (فوق): اسحب رأس التشغيل للتنقل بين الفريمات بسلاسة.
   - مسار لكل نص/تأثير: اسحب الحافتين للبداية/النهاية أو اسحب الجسم لتحريكه. المس مسار لتحديده. */

const MIN_GAP = 0.05;
const LANE_H = 24;
const LANE_GAP = 4;
const RULER_H = 22;
const COLORS = ["#3B82F6", "#2FE6B8", "#FFB020", "#38BDF8", "#F97316", "#A3E635", "#818CF8", "#F472B6"];
const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));
const fmt = (sec: number) => {
  const s = Math.max(0, sec);
  return `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, "0")}`;
};

export interface TimelineLane {
  key: string;
  kind: "text" | "effect";
  id: string;
  label: string;
  start: number;
  end: number;
  active: boolean;
}

export function StickerTimeline({
  animRef,
  lastFrameRef,
  isPlaying,
  setPlaying,
  lanes,
  onSelect,
  onLaneTiming,
  width,
}: {
  animRef: MutableRefObject<AnimationItem | null>;
  lastFrameRef: MutableRefObject<number>;
  isPlaying: boolean;
  setPlaying: (p: boolean) => void;
  lanes: TimelineLane[];
  onSelect: (lane: TimelineLane) => void;
  onLaneTiming: (lane: TimelineLane, patch: { start_fraction?: number; end_fraction?: number }) => void;
  width: number;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const timeRef = useRef<HTMLSpanElement>(null);
  const playingRef = useRef(isPlaying);
  playingRef.current = isPlaying;
  const lanesRef = useRef(lanes);
  lanesRef.current = lanes;
  const scrubbing = useRef(false);
  const resume = useRef(false);
  const drag = useRef<null | { key: string; mode: "l" | "r" | "m"; x: number; s: number; e: number; moved: boolean }>(null);

  const paint = useCallback(() => {
    const a = animRef.current;
    if (!a) return;
    const total = Math.max(1, a.totalFrames - 1);
    const frame = clamp(a.currentFrame, 0, total);
    const p = frame / total;
    if (headRef.current) headRef.current.style.left = `${p * 100}%`;
    if (fillRef.current) fillRef.current.style.width = `${p * 100}%`;
    const fr = a.frameRate || 30;
    if (timeRef.current) timeRef.current.textContent = `${fmt(frame / fr)} / ${fmt(total / fr)}`;
  }, [animRef]);

  useEffect(() => {
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      paint();
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [paint]);

  const seekTo = (frame: number) => {
    const a = animRef.current;
    if (!a) return;
    const f = clamp(frame, 0, Math.max(0, a.totalFrames - 1));
    a.goToAndStop(f, true);
    lastFrameRef.current = f;
    paint();
  };
  const seekFromX = (clientX: number) => {
    const el = trackRef.current;
    const a = animRef.current;
    if (!el || !a) return;
    const r = el.getBoundingClientRect();
    seekTo(clamp((clientX - r.left) / r.width, 0, 1) * Math.max(0, a.totalFrames - 1));
  };

  const onScrubDown = (e: RPE<HTMLDivElement>) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    scrubbing.current = true;
    resume.current = playingRef.current;
    if (playingRef.current) {
      animRef.current?.pause();
      setPlaying(false);
    }
    seekFromX(e.clientX);
  };
  const onScrubMove = (e: RPE<HTMLDivElement>) => {
    if (scrubbing.current) seekFromX(e.clientX);
  };
  const onScrubUp = (e: RPE<HTMLDivElement>) => {
    if (!scrubbing.current) return;
    scrubbing.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (resume.current) {
      animRef.current?.goToAndPlay(lastFrameRef.current, true);
      setPlaying(true);
    }
  };

  const toggle = () => {
    const a = animRef.current;
    if (!a) return;
    if (playingRef.current) a.pause();
    else a.play();
    setPlaying(!playingRef.current);
  };
  const step = (d: number) => {
    const a = animRef.current;
    if (!a) return;
    if (playingRef.current) {
      a.pause();
      setPlaying(false);
    }
    seekTo(Math.round(a.currentFrame) + d);
  };

  const clipDown = (lane: TimelineLane, mode: "l" | "r" | "m") => (e: RPE<HTMLDivElement>) => {
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { key: lane.key, mode, x: e.clientX, s: lane.start, e: lane.end, moved: false };
    if (!lane.active) onSelect(lane);
  };
  const clipMove = (e: RPE<HTMLDivElement>) => {
    const d = drag.current;
    const el = trackRef.current;
    if (!d || !el) return;
    const lane = lanesRef.current.find((l) => l.key === d.key);
    if (!lane) return;
    const dp = (e.clientX - d.x) / el.getBoundingClientRect().width;
    if (Math.abs(e.clientX - d.x) > 2) d.moved = true;
    if (!d.moved) return;
    let s = d.s;
    let en = d.e;
    if (d.mode === "l") s = clamp(s + dp, 0, en - MIN_GAP);
    else if (d.mode === "r") en = clamp(en + dp, s + MIN_GAP, 1);
    else {
      const w = en - s;
      s = clamp(s + dp, 0, 1 - w);
      en = s + w;
    }
    onLaneTiming(lane, { start_fraction: s, end_fraction: en });
  };
  const clipUp = (e: RPE<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const accent = "var(--tg-theme-button-color, #5288c1)";
  const handleStyle: React.CSSProperties = {
    position: "absolute", top: 0, bottom: 0, width: 16, display: "flex",
    alignItems: "center", justifyContent: "center", cursor: "ew-resize", touchAction: "none",
  };
  const lanesH = lanes.length ? lanes.length * (LANE_H + LANE_GAP) : 0;
  const trackH = RULER_H + 4 + lanesH + (lanes.length ? 2 : 6);

  return (
    <div className="mx-auto select-none" dir="ltr" style={{ width, maxWidth: "100%" }}>
      <div className="flex items-center gap-2 mb-1.5">
        <button type="button" onClick={() => step(-1)} aria-label="فريم للخلف" className="w-8 h-8 rounded-full flex items-center justify-center text-tg-hint active:scale-90 transition-transform">
          <SkipBack size={15} />
        </button>
        <button
          type="button"
          onClick={toggle}
          aria-label={isPlaying ? "إيقاف" : "تشغيل"}
          className="w-9 h-9 rounded-full flex items-center justify-center text-white active:scale-90 transition-transform"
          style={{ background: "rgba(255,255,255,.12)", border: "1px solid rgba(255,255,255,.16)" }}
        >
          {isPlaying ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" style={{ marginLeft: 2 }} />}
        </button>
        <button type="button" onClick={() => step(1)} aria-label="فريم للأمام" className="w-8 h-8 rounded-full flex items-center justify-center text-tg-hint active:scale-90 transition-transform">
          <SkipForward size={15} />
        </button>
        <span ref={timeRef} className="ms-auto text-[11px] text-tg-hint tabular-nums font-mono">
          0:00.0 / 0:00.0
        </span>
      </div>

      <div
        ref={trackRef}
        className="relative rounded-xl"
        style={{ height: trackH, background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.09)" }}
      >
        <div
          className="absolute inset-x-0 top-0 cursor-pointer rounded-t-xl"
          style={{ height: RULER_H, touchAction: "none", background: "linear-gradient(to bottom, rgba(255,255,255,.06), transparent)" }}
          onPointerDown={onScrubDown}
          onPointerMove={onScrubMove}
          onPointerUp={onScrubUp}
          onPointerCancel={onScrubUp}
          role="slider"
          aria-label="موضع التشغيل"
        >
          <div className="absolute inset-0 pointer-events-none" style={{
            backgroundImage: "repeating-linear-gradient(to right, rgba(255,255,255,.28) 0 1px, transparent 1px 10%)",
            backgroundSize: "100% 40%", backgroundRepeat: "no-repeat", backgroundPosition: "bottom",
          }} />
          <div ref={fillRef} className="absolute left-0 bottom-0 h-[2px] pointer-events-none" style={{ background: accent, opacity: 0.8 }} />
        </div>

        {lanes.map((lane, i) => {
          const c = COLORS[i % COLORS.length];
          return (
            <div key={lane.key} className="absolute inset-x-0" style={{ top: RULER_H + 4 + i * (LANE_H + LANE_GAP), height: LANE_H }}>
              <div
                className="absolute top-0 bottom-0 rounded-lg overflow-hidden"
                style={{
                  left: `${lane.start * 100}%`,
                  width: `${(lane.end - lane.start) * 100}%`,
                  background: `linear-gradient(135deg, ${c}${lane.active ? "cc" : "55"}, ${c}${lane.active ? "88" : "33"})`,
                  border: `1px solid ${lane.active ? "rgba(255,255,255,.7)" : "rgba(255,255,255,.25)"}`,
                  boxShadow: lane.active ? `0 0 0 1px ${c}, 0 2px 10px -2px rgba(0,0,0,.6)` : "none",
                  cursor: "grab",
                  touchAction: "none",
                }}
                onPointerDown={clipDown(lane, "m")}
                onPointerMove={clipMove}
                onPointerUp={clipUp}
                onPointerCancel={clipUp}
              >
                <div style={{ ...handleStyle, left: 0, background: "rgba(255,255,255,.22)" }} onPointerDown={clipDown(lane, "l")} onPointerMove={clipMove} onPointerUp={clipUp} onPointerCancel={clipUp}>
                  <span style={{ width: 2, height: 11, borderRadius: 2, background: "#fff" }} />
                </div>
                <div style={{ ...handleStyle, right: 0, background: "rgba(255,255,255,.22)" }} onPointerDown={clipDown(lane, "r")} onPointerMove={clipMove} onPointerUp={clipUp} onPointerCancel={clipUp}>
                  <span style={{ width: 2, height: 11, borderRadius: 2, background: "#fff" }} />
                </div>
                <span className="absolute inset-y-0 flex items-center justify-center text-[10px] text-white/95 pointer-events-none px-5 truncate" style={{ left: 0, right: 0 }} dir="auto">
                  {lane.label}
                </span>
              </div>
            </div>
          );
        })}

        <div ref={headRef} className="absolute top-0 bottom-0 pointer-events-none" style={{ left: "0%", width: 0 }}>
          <div className="absolute top-0 bottom-0" style={{ left: -1, width: 2, background: "#fff", boxShadow: "0 0 0 1px rgba(0,0,0,.35)" }} />
          <div className="absolute" style={{ top: -3, left: -7, width: 14, height: 14, borderRadius: 4, background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,.5)", clipPath: "polygon(0 0,100% 0,100% 55%,50% 100%,0 55%)" }} />
        </div>
      </div>
    </div>
  );
}
